import { Component, OnInit, OnDestroy, Inject, ViewChild, ElementRef, AfterViewChecked, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { SignalRService } from 'src/app/shared/services/signalr.service';
import { UserService } from 'src/app/shared/services/user.service';
import { MinimizedMeetingService } from '../services/minimized-meeting.service';
import { MeetingSummaryDialogComponent } from '../meeting-summary-dialog/meeting-summary-dialog.component';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { BidDocumentsDialogComponent } from '../bid-documents-dialog/bid-documents-dialog.component';
import { MessageDialogComponent } from 'src/app/shared/components/message-dialog/message-dialog.component';

interface AgendaBidUnsealDocument {
  attachmentId?: string;
  attachmentFor?: string;
  name?: string;
  fileContent?: string;
  filePath?: string;
  url?: string;
  downloadUrl?: string;
  mimeType?: string;
  fileSize?: number;
  documentExpiryDate?: string;
  isUnsealed: boolean;
}

interface AgendaBidUnsealItem {
  bidId: string;
  vendorId?: string;
  vendorName?: string;
  bidAmount?: number;
  bidValidTill?: string;
  hasTechnicalDocument: boolean;
  technicalUnsealed: boolean;
  hasCommercialDocument: boolean;
  commercialUnsealed: boolean;
  technicalEvaluationDecision?: string;
  isTechnicallyRejected?: boolean;
  evaluations?: Array<{ evaluationId?: string; evaluationType?: string; status?: string; decision?: string; comments?: string; rejectionReason?: string }>;
  technicalDocuments?: AgendaBidUnsealDocument[];
  commercialDocuments?: AgendaBidUnsealDocument[];
}

interface UnsealVoteView {
  bidId: string;
  userId: string;
  userName?: string;
  documentScope: string;
  votedAt?: string;
}

@Component({
  selector: 'app-join-meeting-dialog',
  templateUrl: './join-meeting-dialog.component.html',
  styleUrls: ['./join-meeting-dialog.component.scss']
})
export class JoinMeetingDialogComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('agendaItemsList', { static: false }) agendaItemsList!: ElementRef;
  commentForm: FormGroup;
  meetingTitle: string = '';
  meetingId: string = '';
  agendaItems: any[] = [];
  currentAgendaIndex: number = 0;
  agendaComments: Map<number, string> = new Map();
  acceptedAgendaItems: Set<number> = new Set();
  rejectedAgendaItems: Set<number> = new Set();
  deferredAgendaItems: Set<number> = new Set();
  private shouldScroll: boolean = false;
  private subscriptions: Subscription[] = [];
  private isMinimizing: boolean = false;
  private summaryDialogOpened: boolean = false; // Flag to prevent opening summary dialog twice
  currentAgendaItemAttachments: Array<{ name: string; originalName?: string; url: string; size: string; fileType: string }> = [];
  isLoadingAttachments: boolean = false;
  unsealBids: AgendaBidUnsealItem[] = [];
  isLoadingUnsealBids: boolean = false;
  evaluationReviewItems: any[] = [];
  isLoadingEvaluationReviews: boolean = false;
  awardBids: any[] = [];
  isLoadingAwardBids: boolean = false;
  unsealVotes: Map<string, UnsealVoteView[]> = new Map();
  downloadingUnsealDocuments: Set<string> = new Set();
  agendaWorkflowActions = new Map<number, any[]>();

  openBidDocumentsDialog(bid: { bidId?: string; vendorName?: string; vendorId?: string }): void {
    if (!bid?.bidId) return;
    this.dialog.open(BidDocumentsDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      data: { bidId: bid.bidId, vendorName: bid.vendorName || bid.vendorId }
    });
  }

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<JoinMeetingDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private dialog: MatDialog,
    private signalRService: SignalRService,
    private userService: UserService,
    private minimizedMeetingService: MinimizedMeetingService,
    private toasterService: ToasterService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {
    this.meetingTitle = data?.meetingTitle || '';
    this.meetingId = data?.meetingId || '';
    this.agendaItems = data?.agendaItems || [];
    
    // Sort agenda items by Order property (ascending - Item #1 first, Item #2 second, etc.)
    this.agendaItems.sort((a: any, b: any) => {
      const orderA = a.order || a.Order || 999;
      const orderB = b.order || b.Order || 999;
      return orderA - orderB;
    });
    
    this.currentAgendaIndex = data?.currentAgendaIndex || 0;
    
    this.commentForm = this.fb.group({
      comment: ['']
    });

    (data?.agendaComments || []).forEach((entry: any) => {
      this.agendaComments.set(Number(entry.agendaIndex), entry.comment || '');
    });
    (data?.acceptedAgendaItems || []).forEach((index: number) => this.acceptedAgendaItems.add(Number(index)));
    (data?.rejectedAgendaItems || []).forEach((index: number) => this.rejectedAgendaItems.add(Number(index)));
    (data?.deferredAgendaItems || []).forEach((index: number) => this.deferredAgendaItems.add(Number(index)));

    const restoredComment = this.agendaComments.get(this.currentAgendaIndex);
    if (restoredComment !== undefined) {
      this.commentForm.patchValue({ comment: restoredComment });
    }
  }

  ngOnInit(): void {
    // Load saved comment for current agenda item if exists
    if (this.agendaComments.has(this.currentAgendaIndex)) {
      this.commentForm.patchValue({ comment: this.agendaComments.get(this.currentAgendaIndex) });
    }

    // Get user info for use throughout ngOnInit
    const userId = this.userService.user?.Id || '';
    const userName = this.userService.user?.FullName || 'User';

    // Join the meeting session when dialog opens
    if (this.meetingId) {
      if (userId) {
        // Ensure SignalR connection is established before joining
        this.ensureSignalRConnectionAndJoin();
      } else {
        console.error('❌ [JoinMeetingDialog] Cannot join meeting: User ID is missing');
      }
    }

    // Listen for meeting session state (sent when joining) to restore saved answers
    const sessionStateSub = this.signalRService.meetingSessionState$.subscribe(
      (data) => {
        if (data.meetingId === this.meetingId) {
          console.log('Meeting session state received, restoring saved answers:', data);
          this.restoreSavedAnswers(data.userAgendaData);
          // Navigate to current agenda index if different
          if (data.currentAgendaIndex !== this.currentAgendaIndex) {
            this.selectAgendaItem(data.currentAgendaIndex);
          } else {
            // Even if index hasn't changed, load attachments for current agenda item
            this.loadAttachmentsForCurrentAgenda();
            this.loadUnsealBidsForCurrentAgenda();
          }
        }
      }
    );
    this.subscriptions.push(sessionStateSub);

    // Listen for session TC Decisions
    const allTCDecisionsSub = this.signalRService.allTCDecisions$.subscribe(
      (data) => {
        if (data.meetingId === this.meetingId && data.decisions) {
          console.log('Loaded TC Decisions from SignalR session (member view):', data.decisions);
          Object.keys(data.decisions).forEach((key) => {
            const agendaIndex = parseInt(key);
            const decision = data.decisions[key];
            if (this.agendaItems[agendaIndex]) {
              // Update the agenda item with latest TC Decisions from session
              this.agendaItems[agendaIndex].tcDecisionEN = decision.tcDecisionEn || '';
              this.agendaItems[agendaIndex].tcDecisionAR = decision.tcDecisionAr || '';
              this.agendaItems[agendaIndex].tcDecisionEn = decision.tcDecisionEn || '';
              this.agendaItems[agendaIndex].tcDecisionAr = decision.tcDecisionAr || '';
            }
          });
          console.log('Updated agenda items with session TC Decisions');
        }
      }
    );
    this.subscriptions.push(allTCDecisionsSub);

    // Subscribe before requesting so a fast SignalR response cannot be missed.
    if (this.meetingId) {
      this.signalRService.getAllTCDecisions(this.meetingId);
    }

    // Also request user agenda data when dialog opens (in case we missed the session state)
    if (userId && this.meetingId) {
      setTimeout(() => {
        this.signalRService.getUserAgendaData(this.meetingId, userId);
      }, 500);
    }

    // Listen for user agenda data (for rejoining)
    const userAgendaDataSub = this.signalRService.userAgendaData$.subscribe(
      (data) => {
        if (data.meetingId === this.meetingId) {
          console.log('User agenda data received, restoring saved answers:', data);
          this.restoreSavedAnswers(data.userAgendaData);
          // Navigate to current agenda index if different
          if (data.currentAgendaIndex !== this.currentAgendaIndex) {
            this.selectAgendaItem(data.currentAgendaIndex);
          } else {
            // Even if index hasn't changed, load attachments for current agenda item
            this.loadAttachmentsForCurrentAgenda();
          }
        }
      }
    );
    this.subscriptions.push(userAgendaDataSub);

    // Listen for agenda navigation events from admin
    const navSub = this.signalRService.agendaNavigation$.subscribe(
      (navData) => {
        console.log(`📥 [JoinMeetingDialog] Received agenda navigation: Meeting ${navData.meetingId}, Index ${navData.agendaIndex} (Current meeting: ${this.meetingId})`);
        if (navData.meetingId === this.meetingId) {
          console.log(`✅ [JoinMeetingDialog] Navigating to agenda index ${navData.agendaIndex} (was at ${this.currentAgendaIndex})`);
          this.selectAgendaItem(navData.agendaIndex);
          // selectAgendaItem already loads attachments and unseal bids.
        } else {
          console.warn(`⚠️ [JoinMeetingDialog] Ignoring navigation event - meeting ID mismatch. Expected: ${this.meetingId}, Received: ${navData.meetingId}`);
        }
      }
    );
    this.subscriptions.push(navSub);

    // Listen for user kicked from meeting event
    const userKickedFromMeetingSub = this.signalRService.userKickedFromMeeting$.subscribe(
      (data) => {
        if (data.meetingId === this.meetingId) {
          // User was kicked, close the dialog and show message
          this.toasterService.showWarning('You have been removed from this meeting by the hoster.', 'Removed from Meeting');
          this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
          this.dialogRef.close({ action: 'kicked' });
        }
      }
    );
    this.subscriptions.push(userKickedFromMeetingSub);

    // Listen for meeting ended event
    const meetingEndedSub = this.signalRService.meetingEnded$.subscribe(
      (data) => {
        if (data.meetingId === this.meetingId) {
          // First update status to Completed so the toaster filters it out immediately
          this.minimizedMeetingService.updateMeetingStatus(this.meetingId, 'Completed');
          
          // Always remove from minimized list immediately (even if dialog was already opened)
          this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
          // Also remove any meetings with Completed or Cancelled status
          this.minimizedMeetingService.removeMeetingsByStatus(['Completed', '5', 'completed', 'Cancelled', '4', 'cancelled']);
          
          // Only show summary dialog if not already opened
          if (!this.summaryDialogOpened) {
            console.log('Meeting ended, showing summary dialog');
            
            // Set flag to prevent opening dialog twice
            this.summaryDialogOpened = true;
            
            // Close the join meeting dialog
            this.dialogRef.close({ action: 'ended' });
            
            // Show summary dialog for member with their own responses
            const acceptedItems = Array.from(this.acceptedAgendaItems).map(index => ({
              ...this.agendaItems[index],
              comment: this.agendaComments.get(index) || '',
              response: 'Accepted',
              responseIcon: 'check-circle',
              submittedDate: this.formatSubmittedDate()
            }));
            
            const rejectedItems = Array.from(this.rejectedAgendaItems).map(index => ({
              ...this.agendaItems[index],
              comment: this.agendaComments.get(index) || '',
              response: 'Rejected',
              responseIcon: 'times-circle',
              submittedDate: this.formatSubmittedDate()
            }));
            
            const deferredItems = Array.from(this.deferredAgendaItems).map(index => ({
              ...this.agendaItems[index],
              comment: this.agendaComments.get(index) || '',
              response: 'Deferred',
              responseIcon: 'clock',
              submittedDate: this.formatSubmittedDate()
            }));
            
            // Combine all items
            const allResponses = [...acceptedItems, ...rejectedItems, ...deferredItems];
            
            // Show summary dialog
            setTimeout(() => {
              this.dialog.open(MeetingSummaryDialogComponent, {
                width: '1000px',
                maxWidth: '95vw',
                maxHeight: '95vh',
                disableClose: false,
                panelClass: 'meeting-summary-dialog',
                data: {
                  isHoster: false,
                  acceptedItems: acceptedItems,
                  rejectedItems: rejectedItems,
                  deferredItems: deferredItems
                }
              });
            }, 300);
          } else {
            // Dialog already opened, just close the join meeting dialog if it's still open
            if (this.dialogRef && this.dialogRef.componentInstance) {
              this.dialogRef.close({ action: 'ended' });
            }
          }
        }
      }
    );
    this.subscriptions.push(meetingEndedSub);

    // Listen for TC Decision updates from admin
    const tcDecisionUpdatedSub = this.signalRService.tcDecisionUpdated$.subscribe(
      (data) => {
        if (data.meetingId === this.meetingId && data.agendaIndex === this.currentAgendaIndex) {
          console.log('TC Decision updated:', data);
          // Update the current agenda item with new TC Decision values
          if (this.currentAgendaItem) {
            this.currentAgendaItem.tcDecisionEN = data.tcDecisionEn;
            this.currentAgendaItem.tcDecisionEn = data.tcDecisionEn;
            this.currentAgendaItem.TCDecisionEN = data.tcDecisionEn;
            this.currentAgendaItem.tcDecisionAR = data.tcDecisionAr;
            this.currentAgendaItem.tcDecisionAr = data.tcDecisionAr;
            this.currentAgendaItem.TCDecisionAR = data.tcDecisionAr;
          }
          this.cdr.detectChanges();
        }
      }
    );
    this.subscriptions.push(tcDecisionUpdatedSub);

    const memberUnsealVotedSub = this.signalRService.memberUnsealVoted$.subscribe((data) => {
      if (data.meetingId === this.meetingId && data.agendaIndex === this.currentAgendaIndex) {
        this.addOrUpdateUnsealVote(data);
        this.cdr.detectChanges();
      }
    });
    this.subscriptions.push(memberUnsealVotedSub);

    const allUnsealVotesSub = this.signalRService.allUnsealVotes$.subscribe((data) => {
      if (data.meetingId === this.meetingId && data.agendaIndex === this.currentAgendaIndex) {
        this.unsealVotes.clear();
        (data.votes || []).forEach(vote => this.addOrUpdateUnsealVote(vote));
        this.cdr.detectChanges();
      }
    });
    this.subscriptions.push(allUnsealVotesSub);

    const bidDocumentsUnsealedSub = this.signalRService.bidDocumentsUnsealed$.subscribe((data) => {
      if (data.meetingId === this.meetingId && data.agendaIndex === this.currentAgendaIndex) {
        this.loadUnsealBidsForCurrentAgenda();
      }
    });
    this.subscriptions.push(bidDocumentsUnsealedSub);

    const workflowActionSub = this.signalRService.agendaWorkflowActionUpdated$.subscribe((data) => {
      if (data.meetingId === this.meetingId) this.upsertAgendaWorkflowAction(data.agendaIndex, data.action);
    });
    this.subscriptions.push(workflowActionSub);

    const allWorkflowActionsSub = this.signalRService.allAgendaWorkflowActions$.subscribe((data) => {
      if (data.meetingId === this.meetingId) {
        this.agendaWorkflowActions.set(data.agendaIndex, data.actions || []);
        this.cdr.detectChanges();
      }
    });
    this.subscriptions.push(allWorkflowActionsSub);

    this.loadUnsealBidsForCurrentAgenda();
    this.signalRService.getAgendaWorkflowActions(this.meetingId, this.currentAgendaIndex);

  }

  // Restore saved answers from session storage
  private restoreSavedAnswers(userAgendaData: any): void {
    if (!userAgendaData) return;

    // Restore comments
    if (userAgendaData.comments) {
      Object.keys(userAgendaData.comments).forEach((key: string) => {
        const agendaIndex = parseInt(key);
        this.agendaComments.set(agendaIndex, userAgendaData.comments[key]);
      });
    }

    // Restore accepted items
    if (userAgendaData.acceptedItems && Array.isArray(userAgendaData.acceptedItems)) {
      userAgendaData.acceptedItems.forEach((index: number) => {
        this.acceptedAgendaItems.add(index);
        this.rejectedAgendaItems.delete(index); // Remove from rejected if it was there
        this.deferredAgendaItems.delete(index); // Remove from deferred if it was there
      });
    }

    // Restore rejected items
    if (userAgendaData.rejectedItems && Array.isArray(userAgendaData.rejectedItems)) {
      userAgendaData.rejectedItems.forEach((index: number) => {
        this.rejectedAgendaItems.add(index);
        this.acceptedAgendaItems.delete(index); // Remove from accepted if it was there
        this.deferredAgendaItems.delete(index); // Remove from deferred if it was there
      });
    }

    // Restore deferred items
    if (userAgendaData.deferredItems && Array.isArray(userAgendaData.deferredItems)) {
      userAgendaData.deferredItems.forEach((index: number) => {
        this.deferredAgendaItems.add(index);
        this.acceptedAgendaItems.delete(index); // Remove from accepted if it was there
        this.rejectedAgendaItems.delete(index); // Remove from rejected if it was there
      });
    }

    // Restore comment for current agenda item
    if (this.agendaComments.has(this.currentAgendaIndex)) {
      this.commentForm.patchValue({ comment: this.agendaComments.get(this.currentAgendaIndex) });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll && this.agendaItemsList) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        this.scrollToActiveItem();
        this.shouldScroll = false;
      }, 0);
    }
  }

  scrollToActiveItem(): void {
    if (!this.agendaItemsList) return;
    
    const container = this.agendaItemsList.nativeElement;
    const activeItem = container.querySelector(`.agenda-item-tab.active`);
    
    if (activeItem) {
      activeItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }

  get currentAgendaItem(): any {
    return this.agendaItems[this.currentAgendaIndex] || null;
  }

  get hasNext(): boolean {
    return this.currentAgendaIndex < this.agendaItems.length - 1;
  }

  get hasPrevious(): boolean {
    return this.currentAgendaIndex > 0;
  }

  // Check if current user is an Observer (read-only access)
  get isObserver(): boolean {
    const userRole = this.userService.user?.Role || '';
    const roleLower = userRole.toString().toLowerCase().replace(/\s+/g, '');
    return roleLower === 'observer';
  }

  getAgendaDescriptionEN(): string {
    return this.resolveAgendaDescription(this.currentAgendaItem, 'en');
  }

  getAgendaDescriptionAR(): string {
    return this.resolveAgendaDescription(this.currentAgendaItem, 'ar');
  }

  private resolveAgendaDescription(agendaItem: any, language: 'en' | 'ar'): string {
    if (!agendaItem) {
      return '';
    }

    const keys = language === 'en'
      ? ['descriptionEnglish', 'DescriptionEnglish', 'agendaDescriptionEN', 'AgendaDescriptionEN', 'agendaDescriptionEn', 'descriptionEN', 'DescriptionEN']
      : ['descriptionArabic', 'DescriptionArabic', 'agendaDescriptionAR', 'AgendaDescriptionAR', 'agendaDescriptionAr', 'descriptionAR', 'DescriptionAR'];

    for (const key of keys) {
      const value = agendaItem[key];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }

    return '';
  }

  // Helper method to get TC Decision EN from various property name formats
  getTCDecisionEN(): string {
    if (!this.currentAgendaItem) return '';
    return this.currentAgendaItem.tcDecisionEN || 
           this.currentAgendaItem.tcDecisionEn || 
           this.currentAgendaItem.TCDecisionEN ||
           '';
  }

  // Helper method to get TC Decision AR from various property name formats
  getTCDecisionAR(): string {
    if (!this.currentAgendaItem) return '';
    return this.currentAgendaItem.tcDecisionAR || 
           this.currentAgendaItem.tcDecisionAr || 
           this.currentAgendaItem.TCDecisionAR ||
           '';
  }

  getTrustedDecisionHtml(html: string | null | undefined, fallback: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html || fallback);
  }

  selectAgendaItem(index: number): void {
    // Save current comment before switching
    const currentComment = this.commentForm.value.comment || '';
    if (currentComment.trim()) {
      this.agendaComments.set(this.currentAgendaIndex, currentComment);
      // Save comment to SignalR session
      const userId = this.userService.user?.Id || '';
      if (this.meetingId && userId) {
        this.signalRService.saveAgendaComment(this.meetingId, userId, this.currentAgendaIndex, currentComment);
      }
    }
    
    // Switch to new agenda item
    this.currentAgendaIndex = index;
    this.shouldScroll = true;
    
    // Load saved comment for new agenda item if exists
    const savedComment = this.agendaComments.get(this.currentAgendaIndex) || '';
    this.commentForm.patchValue({ comment: savedComment });
    
    // Load attachments for the new agenda item
    this.loadAttachmentsForCurrentAgenda();
    this.loadUnsealBidsForCurrentAgenda();
    this.signalRService.getAgendaWorkflowActions(this.meetingId, this.currentAgendaIndex);
  }

  goToPrevious(): void {
    if (this.hasPrevious) {
      this.selectAgendaItem(this.currentAgendaIndex - 1);
    }
  }

  goToNext(): void {
    if (this.hasNext) {
      this.selectAgendaItem(this.currentAgendaIndex + 1);
    }
  }

  onCancel(): void {
    // Minimize instead of close
    this.minimizeDialog();
  }

  minimizeDialog(): void {
    const currentComment = this.commentForm.get('comment')?.value || '';
    this.agendaComments.set(this.currentAgendaIndex, currentComment);
    const userId = this.userService.user?.Id || '';
    if (this.meetingId && userId) {
      this.signalRService.saveAgendaComment(
        this.meetingId, userId, this.currentAgendaIndex, currentComment
      );
    }

    this.isMinimizing = true;
    
    // Get meeting status from data or assume OnGoing for join dialog
    const status = this.data?.meetingStatus || 'OnGoing';
    
    // Add to minimized meetings
    this.minimizedMeetingService.addMinimizedMeeting({
      meetingId: this.meetingId,
      meetingTitle: this.meetingTitle,
      status: status,
      dialogType: 'join',
      dialogData: {
        meetingTitle: this.meetingTitle,
        agendaItems: this.agendaItems,
        currentAgendaIndex: this.currentAgendaIndex,
        meetingId: this.meetingId,
        meetingStatus: status,
        agendaComments: Array.from(this.agendaComments.entries()).map(([agendaIndex, comment]) => ({
          agendaIndex,
          comment
        })),
        acceptedAgendaItems: Array.from(this.acceptedAgendaItems),
        rejectedAgendaItems: Array.from(this.rejectedAgendaItems),
        deferredAgendaItems: Array.from(this.deferredAgendaItems)
      },
      dialogRef: this.dialogRef
    });

    // Close the dialog (will be restored from minimized state)
    this.dialogRef.close({ action: 'minimized' });
  }


  get currentUnsealScope(): 'technical' | 'commercial' | 'combined' | null {
    const stage = (this.currentAgendaItem?.currentStage || this.currentAgendaItem?.CurrentStage || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!stage.includes('unsealing')) {
      return null;
    }
    if (stage.includes('combined')) {
      return 'combined';
    }
    if (stage.includes('technical')) {
      return 'technical';
    }
    if (stage.includes('commercial')) {
      return 'commercial';
    }
    return null;
  }

  get shouldShowUnsealSection(): boolean {
    return !!this.currentUnsealScope;
  }

  get isEvaluationReviewStage(): boolean {
    return this.getNormalizedCurrentAgendaStage() === 'evaluationreview';
  }

  get isAwardBidStage(): boolean {
    return this.getNormalizedCurrentAgendaStage() === 'awardbid';
  }

  private getNormalizedCurrentAgendaStage(): string {
    return (this.currentAgendaItem?.currentStage || this.currentAgendaItem?.CurrentStage || '')
      .toString().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  get unsealSectionTitle(): string {
    switch (this.currentUnsealScope) {
      case 'technical': return 'Unsealing Technical Documents';
      case 'commercial': return 'Unsealing Commercial Documents';
      case 'combined': return 'Unsealing Combined Documents';
      default: return 'Bid Document Unsealing';
    }
  }

  getAgendaStageClass(stage: any): string {
    const value = (stage || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
    if (value.includes('unsealingtechnical')) return 'stage-unseal-technical';
    if (value.includes('unsealingcommercial')) return 'stage-unseal-commercial';
    if (value.includes('unsealingcombined')) return 'stage-unseal-combined';
    if (value.includes('evaluationreview')) return 'stage-evaluation-review';
    if (value.includes('awardbid')) return 'stage-award-bid';
    return '';
  }

  private getCurrentAgendaItemId(): number {
    return Number(this.currentAgendaItem?.id || this.currentAgendaItem?.ID || this.currentAgendaItem?.agendaItemId || this.currentAgendaItem?.AgendaItemId || 0);
  }

  async loadUnsealBidsForCurrentAgenda(): Promise<void> {
    void this.loadEvaluationReviewsForCurrentAgenda();
    void this.loadAwardBidsForCurrentAgenda();
    if (!this.shouldShowBidContext) {
      this.unsealBids = [];
      this.unsealVotes.clear();
      return;
    }

    const agendaItemId = this.getCurrentAgendaItemId();
    if (!agendaItemId) {
      this.unsealBids = [];
      return;
    }

    this.isLoadingUnsealBids = true;
    try {
      const url = `${environment.API_URL}/api/Meetings/agenda/${agendaItemId}/unseal-bids`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      this.unsealBids = response?.data || response?.Data || [];
      if (this.meetingId) {
        await this.signalRService.getAllUnsealVotes(this.meetingId, this.currentAgendaIndex);
      }
    } catch (error) {
      console.error('Failed to load agenda bids for unsealing:', error);
      this.unsealBids = [];
    } finally {
      this.isLoadingUnsealBids = false;
    }
  }

  async loadEvaluationReviewsForCurrentAgenda(): Promise<void> {
    if (!this.shouldShowBidContext) {
      this.evaluationReviewItems = [];
      return;
    }
    const agendaItemId = this.getCurrentAgendaItemId();
    if (!agendaItemId) return;
    this.isLoadingEvaluationReviews = true;
    try {
      const response: any = await lastValueFrom(this.apiService.getByUrl(`${environment.API_URL}/api/Meetings/agenda/${agendaItemId}/evaluation-reviews`));
      this.evaluationReviewItems = response?.data || response?.Data || [];
    } catch (error) {
      console.error('Failed to load evaluation reviews:', error);
      this.evaluationReviewItems = [];
    } finally {
      this.isLoadingEvaluationReviews = false;
    }
  }

  async downloadEvaluationReport(report: any): Promise<void> {
    if (!report?.attachmentId) return;
    await this.downloadAttachment({
      name: report.name || 'Evaluation Report',
      url: `${environment.API_URL}/api/Documents/DownloadDocument/${report.attachmentId}`,
      fileType: report.mimeType || 'application/octet-stream'
    });
  }

  async loadAwardBidsForCurrentAgenda(): Promise<void> {
    if (!this.isAwardBidStage) {
      this.awardBids = [];
      return;
    }
    const agendaItemId = this.getCurrentAgendaItemId();
    if (!agendaItemId) return;
    this.isLoadingAwardBids = true;
    try {
      const response: any = await lastValueFrom(this.apiService.getByUrl(`${environment.API_URL}/api/Meetings/agenda/${agendaItemId}/award-bids`));
      this.awardBids = response?.data || response?.Data || [];
    } catch (error) {
      console.error('Failed to load award bids:', error);
      this.awardBids = [];
    } finally {
      this.isLoadingAwardBids = false;
    }
  }

  get shouldShowBidContext(): boolean {
    return this.shouldShowUnsealSection || this.isEvaluationReviewStage || this.isAwardBidStage;
  }

  get visibleUnsealBidsForMember(): AgendaBidUnsealItem[] {
    return this.unsealBids || [];
  }

  getAgendaWorkflowAction(actionType: string, entityId: string): any {
    return (this.agendaWorkflowActions.get(this.currentAgendaIndex) || []).find(action => action.actionType === actionType && action.entityId === entityId);
  }

  getEvaluationReviewActionText(evaluation: any): string {
    const action = this.getAgendaWorkflowAction('evaluation-review', evaluation.evaluationId || evaluation.EvaluationId);
    return action ? (action.isApproved ? 'Accepted' : 'Change Request') : (evaluation.reviewAction || evaluation.ReviewAction || '-');
  }

  getEvaluationReviewComments(evaluation: any): string {
    const action = this.getAgendaWorkflowAction('evaluation-review', evaluation.evaluationId || evaluation.EvaluationId);
    return action?.comments || evaluation.reviewComments || evaluation.ReviewComments || '-';
  }

  hasSecretaryChangeRequest(): boolean {
    return this.evaluationReviewItems.some(evaluation => this.getEvaluationReviewActionText(evaluation) === 'Change Request');
  }

  showEvaluatorComments(evaluation: any): void {
    this.dialog.open(MessageDialogComponent, {
      width: '500px',
      data: {
        title: 'Evaluator Comments',
        message: evaluation?.comments || evaluation?.Comments || 'No evaluator comments were provided.',
        type: 'info',
        buttonText: 'Close'
      }
    });
  }

  getCommitteeBidStatus(bid: any): string {
    const selected = (this.agendaWorkflowActions.get(this.currentAgendaIndex) || []).find(action => action.actionType === 'award');
    return selected ? (selected.entityId === bid.bidId ? 'Awarded' : 'Regret') : (bid.tCComitteeStatus || bid.TCComitteeStatus || 'InProgress');
  }

  private upsertAgendaWorkflowAction(agendaIndex: number, action: any): void {
    const actions = (this.agendaWorkflowActions.get(agendaIndex) || []).filter(existing => {
      if (action.actionType === 'award' && existing.actionType === 'award') {
        return false;
      }

      return !(existing.actionType === action.actionType && existing.entityId === action.entityId);
    });
    actions.push(action);
    this.agendaWorkflowActions.set(agendaIndex, actions);
    this.cdr.detectChanges();
  }

  addOrUpdateUnsealVote(vote: any): void {
    if (!vote?.bidId || !vote?.userId) {
      return;
    }

    const currentVotes = this.unsealVotes.get(vote.bidId) || [];
    const updatedVotes = currentVotes.filter(v => v.userId?.toString() !== vote.userId?.toString());
    updatedVotes.push({
      bidId: vote.bidId,
      userId: vote.userId,
      userName: vote.userName,
      documentScope: vote.documentScope,
      votedAt: vote.votedAt
    });
    this.unsealVotes.set(vote.bidId, updatedVotes);
  }

  hasCurrentUserUnsealVoted(bid: AgendaBidUnsealItem): boolean {
    const userId = this.userService.user?.Id?.toString() || '';
    return (this.unsealVotes.get(bid.bidId) || []).some(vote => vote.userId?.toString() === userId);
  }

  async voteToUnsealBid(bid: AgendaBidUnsealItem): Promise<void> {
    if (this.isObserver || this.isCommercialUnsealBlockedByTechnicalRejection(bid) || this.hasCurrentUserUnsealVoted(bid) || this.isBidUnsealedForScope(bid) || !this.hasBidDocumentForScope(bid)) {
      return;
    }

    const userId = this.userService.user?.Id?.toString() || '';
    if (!this.meetingId || !userId || !this.currentUnsealScope) {
      return;
    }

    await this.signalRService.saveUnsealVote(this.meetingId, userId, this.currentAgendaIndex, bid.bidId, this.currentUnsealScope);
    this.toasterService.showSuccess('Unseal confirmation submitted.', 'Success');
  }

  hasBidDocumentForScope(bid: AgendaBidUnsealItem): boolean {
    if (this.currentUnsealScope === 'technical') return bid.hasTechnicalDocument;
    if (this.currentUnsealScope === 'commercial') return bid.hasCommercialDocument;
    return bid.hasTechnicalDocument || bid.hasCommercialDocument;
  }

  isBidUnsealedForScope(bid: AgendaBidUnsealItem): boolean {
    if (this.currentUnsealScope === 'technical') return bid.hasTechnicalDocument && bid.technicalUnsealed;
    if (this.currentUnsealScope === 'commercial') return bid.hasCommercialDocument && bid.commercialUnsealed;
    return (!bid.hasTechnicalDocument || bid.technicalUnsealed) && (!bid.hasCommercialDocument || bid.commercialUnsealed);
  }

  getScopeStatusText(bid: AgendaBidUnsealItem): string {
    if (!this.hasBidDocumentForScope(bid)) return 'No matching documents';
    return this.isBidUnsealedForScope(bid) ? 'Unsealed' : 'Sealed';
  }


  getVisibleUnsealDocuments(bid: AgendaBidUnsealItem): AgendaBidUnsealDocument[] {
    const documents: AgendaBidUnsealDocument[] = [];
    if (this.currentUnsealScope === 'technical' || this.currentUnsealScope === 'combined') {
      documents.push(...((bid.technicalDocuments || []).filter(document => document.isUnsealed)));
    }
    if (this.currentUnsealScope === 'commercial' || this.currentUnsealScope === 'combined') {
      documents.push(...((bid.commercialDocuments || []).filter(document => document.isUnsealed)));
    }
    return documents;
  }

  getUnsealDocumentKey(document: AgendaBidUnsealDocument): string {
    return document?.attachmentId || document?.filePath || document?.name || '';
  }

  isCommercialUnsealBlockedByTechnicalRejection(bid: AgendaBidUnsealItem): boolean {
    if (this.currentUnsealScope !== 'commercial' && this.currentUnsealScope !== 'combined') {
      return false;
    }

    const technicalDecision = (bid.isTechnicallyRejected
      || (bid.technicalEvaluationDecision || '').toLowerCase().includes('reject')
      || (bid.evaluations || []).some((evaluation: any) =>
        (evaluation.evaluationType || '').toLowerCase() === 'technical'
        && (evaluation.decision || '').toLowerCase().includes('reject')));

    return technicalDecision;
  }

  isUnsealDocumentDownloading(document: AgendaBidUnsealDocument): boolean {
    const key = this.getUnsealDocumentKey(document);
    return !!key && this.downloadingUnsealDocuments.has(key);
  }

  async openUnsealDocument(document: AgendaBidUnsealDocument): Promise<void> {
    const key = this.getUnsealDocumentKey(document);
    if (document?.attachmentId && key) {
      this.downloadingUnsealDocuments.add(key);
      try {
        await this.downloadAttachment({
          name: document.name || document.attachmentFor || 'Document',
          url: `${environment.API_URL}/api/Documents/DownloadDocument/${document.attachmentId}`,
          fileType: document.mimeType || 'application/octet-stream'
        });
      } finally {
        this.downloadingUnsealDocuments.delete(key);
      }
      return;
    }

    this.toasterService.showError('Document attachment ID is not available.', 'Document Error');
  }

  formatUnsealFileSize(value: any): string {
    const size = Number(value || 0);
    if (!size) return '';
    if (size >= 1024) return `${(size / 1024).toFixed(2)} MB`;
    return `${size.toFixed(2)} KB`;
  }

  getUnsealDocumentSummary(bid: AgendaBidUnsealItem): string {
    if (this.currentUnsealScope === 'technical') return bid.hasTechnicalDocument ? 'Technical proposal' : 'No technical proposal';
    if (this.currentUnsealScope === 'commercial') return bid.hasCommercialDocument ? 'Commercial proposal' : 'No commercial proposal';
    const docs: string[] = [];
    docs.push(bid.hasTechnicalDocument ? 'Technical proposal' : 'No technical proposal');
    docs.push(bid.hasCommercialDocument ? 'Commercial proposal' : 'No commercial proposal');
    return docs.join(' | ');
  }

  getUnsealVoteCount(bid: AgendaBidUnsealItem): number {
    if (this.isBidUnsealedForScope(bid)) {
      return this.getRequiredUnsealCount();
    }

    return (this.unsealVotes.get(bid.bidId) || []).length;
  }

  getRequiredUnsealCount(): number {
    return (this.data?.meetingMembers || [])
      .filter((member: any) => !!(member.userId || member.UserId || member.id || member.user?.id))
      .filter((member: any) => {
        const role = (member.role || member.Role || '').toString().toLowerCase().replace(/\s/g, '');
        return role !== 'secretary' && role !== 'observer';
      })
      .length;
  }

  getUnsealConfirmationText(bid: AgendaBidUnsealItem): string {
    if (this.isBidUnsealedForScope(bid)) {
      return 'Unsealed';
    }

    return `${this.getUnsealVoteCount(bid)} confirmed`;
  }

  formatBidDate(value: any): string {
    if (!value) return '-';
    const date = new Date(value);
    return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-GB');
  }

  formatBidAmount(value: any): string {
    const amount = Number(value);
    return isNaN(amount) ? '-' : amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  onReject(): void {
    // Save current comment but don't mark as accepted
    const comment = this.commentForm.value.comment || '';
    this.agendaComments.set(this.currentAgendaIndex, comment);
    // Remove from accepted and deferred items if it was previously accepted/deferred
    this.acceptedAgendaItems.delete(this.currentAgendaIndex);
    this.deferredAgendaItems.delete(this.currentAgendaIndex);
    // Add to rejected items
    this.rejectedAgendaItems.add(this.currentAgendaIndex);
    
    const formData = {
      action: 'reject',
      comment: comment,
      agendaItem: this.currentAgendaItem,
      agendaIndex: this.currentAgendaIndex
    };
    console.log('Rejecting agenda item:', formData);
    
    // Save answer to SignalR session storage
    const userId = this.userService.user?.Id || '';
    if (this.meetingId && userId) {
      this.signalRService.saveAgendaAnswer(
        this.meetingId,
        userId,
        this.currentAgendaIndex,
        'reject',
        comment
      );
    }
    
    // Do NOT navigate automatically - wait for admin to navigate
    // Admin will control navigation via Next/Back buttons
  }

  onAccept(): void {
    // Save current comment and mark as accepted
    const comment = this.commentForm.value.comment || '';
    this.agendaComments.set(this.currentAgendaIndex, comment);
    this.acceptedAgendaItems.add(this.currentAgendaIndex);
    // Remove from rejected and deferred items if it was previously rejected/deferred
    this.rejectedAgendaItems.delete(this.currentAgendaIndex);
    this.deferredAgendaItems.delete(this.currentAgendaIndex);
    
    const formData = {
      action: 'accept',
      comment: comment,
      agendaItem: this.currentAgendaItem,
      agendaIndex: this.currentAgendaIndex
    };
    console.log('Accepting agenda item:', formData);
    
    // Save answer to SignalR session storage
    const userId = this.userService.user?.Id || '';
    if (this.meetingId && userId) {
      this.signalRService.saveAgendaAnswer(
        this.meetingId,
        userId,
        this.currentAgendaIndex,
        'accept',
        comment
      );
    }
    
    // Do NOT navigate automatically - wait for admin to navigate
    // Admin will control navigation via Next/Back buttons
  }

  onDefer(): void {
    // Save current comment and mark as deferred
    const comment = this.commentForm.value.comment || '';
    this.agendaComments.set(this.currentAgendaIndex, comment);
    this.deferredAgendaItems.add(this.currentAgendaIndex);
    // Remove from accepted and rejected items if it was previously accepted/rejected
    this.acceptedAgendaItems.delete(this.currentAgendaIndex);
    this.rejectedAgendaItems.delete(this.currentAgendaIndex);
    
    const formData = {
      action: 'defer',
      comment: comment,
      agendaItem: this.currentAgendaItem,
      agendaIndex: this.currentAgendaIndex
    };
    console.log('Deferring agenda item:', formData);
    
    // Save answer to SignalR session storage
    const userId = this.userService.user?.Id || '';
    if (this.meetingId && userId) {
      this.signalRService.saveAgendaAnswer(
        this.meetingId,
        userId,
        this.currentAgendaIndex,
        'defer',
        comment
      );
    }
    
    // Do NOT navigate automatically - wait for admin to navigate
    // Admin will control navigation via Next/Back buttons
  }

  onLeaveMeeting(): void {
    // Confirm before leaving
    const confirmDialog = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Leave Meeting',
        message: 'Are you sure you want to leave this meeting? You will be removed from the meeting session.',
        confirmText: 'Leave',
        cancelText: 'Cancel'
      }
    });

    confirmDialog.afterClosed().subscribe(result => {
      if (result) {
        // Leave the meeting via SignalR
        const userId = this.userService.user?.Id || '';
        
        if (this.meetingId && userId) {
          this.signalRService.leaveMeeting(this.meetingId, userId).then(() => {
            console.log('Left meeting:', this.meetingId);
            // Remove from minimized list
            this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
            // Close the dialog
            this.dialogRef.close({ action: 'left' });
          }).catch(error => {
            console.error('Error leaving meeting:', error);
            // Close the dialog anyway
            this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
            this.dialogRef.close({ action: 'left' });
          });
        } else {
          // Close the dialog if no meeting ID
          this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
          this.dialogRef.close({ action: 'left' });
        }
      }
    });
  }

  private formatSubmittedDate(): string {
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${dateStr} ${timeStr}`;
  }

  showSummaryDialog(): void {
    const formattedDate = this.formatSubmittedDate();
    
    // Collect accepted and rejected items with user info
    const acceptedItems = Array.from(this.acceptedAgendaItems).map(index => ({
      ...this.agendaItems[index],
      comment: this.agendaComments.get(index) || '',
      response: 'Accepted',
      responseIcon: 'check-circle',
      submittedDate: formattedDate,
      agendaIndex: index
    }));
    
    const rejectedItems = Array.from(this.rejectedAgendaItems).map(index => ({
      ...this.agendaItems[index],
      comment: this.agendaComments.get(index) || '',
      response: 'Rejected',
      responseIcon: 'times-circle',
      submittedDate: formattedDate,
      agendaIndex: index
    }));

    // Close current dialog first
    this.dialogRef.close({ action: 'completed' });

    // Open summary dialog
    const summaryDialogRef = this.dialog.open(MeetingSummaryDialogComponent, {
      width: '1000px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: false,
      panelClass: 'meeting-summary-dialog',
      data: {
        acceptedItems: acceptedItems,
        rejectedItems: rejectedItems
      }
    });

    summaryDialogRef.afterClosed().subscribe(result => {
      console.log('Summary dialog closed');
    });
  }

  goBackToAgendaList(): void {
    this.dialogRef.close({ action: 'back' });
  }

  async loadAttachmentsForCurrentAgenda(): Promise<void> {
    const currentAgenda = this.currentAgendaItem;
    if (!currentAgenda) {
      this.currentAgendaItemAttachments = [];
      return;
    }

    // Get agenda item ID (could be id or agendaItemId)
    const agendaItemId = currentAgenda.id || currentAgenda.agendaItemId;
    if (!agendaItemId) {
      this.currentAgendaItemAttachments = [];
      return;
    }

    this.isLoadingAttachments = true;
    this.currentAgendaItemAttachments = [];

    try {
      // Fetch documents for this agenda item
      const documentsUrl = `${environment.API_URL}/api/Documents/GetDocuments`;
      const documentsDto = {
        applicationId: agendaItemId.toString(),
        processName: 'AgendaItem',
        processID: agendaItemId.toString()
      };

      const documentsResponse: any = await lastValueFrom(this.apiService.postByUrl(documentsUrl, documentsDto));

      if (documentsResponse?.isSuccess && documentsResponse?.data) {
        if (Array.isArray(documentsResponse.data) && documentsResponse.data.length > 0) {
          const documents = documentsResponse.data;
          
          // Use direct blob access (SAS URLs) - same as in admin-meeting-dialog
          const USE_DIRECT_BLOB_ACCESS = true;
          
          // Process documents and fetch file sizes
          const attachmentPromises = documents
            .filter((doc: any) => {
              if (USE_DIRECT_BLOB_ACCESS) {
                return doc.fileContent && doc.name;
              } else {
                return doc.attachmentId && doc.name;
              }
            })
            .map(async (doc: any) => {
              // Format file size
              const formatFileSize = (bytes: number): string => {
                if (!bytes || bytes === 0) return '0 Bytes';
                const k = 1024;
                const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                const i = Math.floor(Math.log(bytes) / Math.log(k));
                return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
              };

              // Get file URL - use SAS URL if available, otherwise use download endpoint
              let fileUrl: string;
              if (USE_DIRECT_BLOB_ACCESS && doc.fileContent) {
                fileUrl = doc.fileContent; // SAS URL
              } else {
                fileUrl = `${environment.API_URL}/api/Documents/DownloadDocument/${doc.attachmentId}`;
              }

              // Fetch file to get actual size
              let fileSize = 0;
              try {
                const response = await fetch(fileUrl, { method: 'HEAD' });
                const contentLength = response.headers.get('content-length');
                if (contentLength) {
                  fileSize = parseInt(contentLength, 10);
                } else {
                  // If HEAD doesn't work, fetch the blob to get size
                  const blobResponse = await fetch(fileUrl);
                  const blob = await blobResponse.blob();
                  fileSize = blob.size;
                }
              } catch (error) {
                console.warn(`Could not fetch file size for ${doc.name}:`, error);
                // Try to use doc.size if available as fallback
                fileSize = doc.size || doc.fileSize || 0;
              }

              // Clean filename - remove attachment IDs and UUIDs
              // Backend generates: "{vendorId}-{attachmentFor}-{attachmentId}-{uniqueId}.{extension}"
              // Example: "20-AgendaItemAttachment-07ed1d6e-ab75-488f-9681-b47eb74d6c50-43cd02d0-115e-4c3e-9e63-0f1516ea3c22.pdf"
              // Should become: "document.pdf" (just extension, no IDs)
              let cleanFileName = doc.name || 'document';
              
              // Extract file extension
              const extension = cleanFileName.includes('.') ? cleanFileName.split('.').pop() || '' : '';
              
              // Pattern to match UUIDs (8-4-4-4-12 hex format)
              const uuidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi;
              
              // Check if filename contains UUIDs (which indicates it has IDs)
              const hasUUIDs = uuidPattern.test(cleanFileName);
              
              if (hasUUIDs) {
                // Filename contains IDs/UUIDs - use clean generic name with extension
                cleanFileName = extension ? `document.${extension}` : 'document';
              }
              // If no UUIDs found, keep original filename as-is

              return {
                name: cleanFileName,
                originalName: doc.name, // Keep original for display
                url: fileUrl,
                size: formatFileSize(fileSize),
                fileType: doc.mimeType || 'application/octet-stream'
              };
            });

          // Wait for all attachments to be processed
          this.currentAgendaItemAttachments = await Promise.all(attachmentPromises);
        }
      }
    } catch (error) {
      console.error('Error loading attachments:', error);
      this.currentAgendaItemAttachments = [];
    } finally {
      this.isLoadingAttachments = false;
    }
  }

  async previewAttachment(attachment: { name: string; url: string; fileType: string }): Promise<void> {
    try {
      // Detect Safari/iPad - Safari blocks window.open() after async operations
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
                       /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      // For Safari/iPad: Open the original URL directly in a new tab (no blank page)
      if (isSafari) {
        window.open(attachment.url, '_blank', 'noopener,noreferrer');
        return;
      }

      // For Chrome/other browsers: Fetch as blob to prevent downloads
      // Fetch the file as a blob to create an object URL for preview
      // This prevents the browser from downloading the file due to Content-Disposition headers
      const response = await fetch(attachment.url, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const file = new File([blob], attachment.name, { type: attachment.fileType });
      const fileURL = URL.createObjectURL(file);
      
      // Open in new tab with file loaded
      window.open(fileURL, '_blank', 'noopener,noreferrer');
      
      // Don't revoke the blob URL - let browser handle cleanup when tab closes
      // The file-upload component doesn't revoke it either, letting the browser manage it
    } catch (error) {
      console.error('Error previewing attachment:', error);
      this.toasterService.showError('Failed to preview file. Please try downloading instead.', 'Preview Error');
    }
  }

  async downloadAttachment(attachment: { name: string; originalName?: string; url: string; fileType: string }): Promise<void> {
    try {
      // Fetch the file as a blob
      const response = await fetch(attachment.url, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Create a File object with the clean filename (without IDs)
      const file = new File([blob], attachment.name, { type: attachment.fileType });
      
      // Create object URL and trigger download with clean filename
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = attachment.name; // Use clean filename (without IDs)
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up object URL after a short delay
      setTimeout(() => {
        URL.revokeObjectURL(fileURL);
      }, 100);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      this.toasterService.showError('Failed to download file. Please try again.', 'Download Error');
    }
  }

  private async ensureSignalRConnectionAndJoin(): Promise<void> {
    const userId = this.userService.user?.Id || '';
    const userName = this.userService.user?.FullName || 'User';
    
    // Check if SignalR is connected
    if (!this.signalRService.isConnected()) {
      console.log('🔄 [JoinMeetingDialog] SignalR not connected, starting connection...');
      try {
        await this.signalRService.startConnection();
        // Wait a bit for connection to stabilize
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('❌ [JoinMeetingDialog] Failed to start SignalR connection:', error);
        this.toasterService.showError('Failed to connect to meeting. Please refresh the page.', 'Connection Error');
        return;
      }
    }

    // Verify connection is established
    if (!this.signalRService.isConnected()) {
      console.error('❌ [JoinMeetingDialog] SignalR connection still not established after start attempt');
      //this.toasterService.showError('Connection failed. Please refresh the page.', 'Connection Error');
      return;
    }

    // Join the meeting with retry logic
    let retryCount = 0;
    const maxRetries = 3;
    
    while (retryCount < maxRetries) {
      try {
        console.log(`🔄 [JoinMeetingDialog] Attempting to join meeting ${this.meetingId} (attempt ${retryCount + 1}/${maxRetries})...`);
        await this.signalRService.joinMeeting(this.meetingId, userId, userName);
        console.log(`✅ [JoinMeetingDialog] Successfully joined meeting: ${this.meetingId}`);
        return; // Success, exit retry loop
      } catch (error) {
        retryCount++;
        console.error(`❌ [JoinMeetingDialog] Error joining meeting (attempt ${retryCount}/${maxRetries}):`, error);
        
        if (retryCount < maxRetries) {
          // Wait before retry (exponential backoff)
          const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 5000);
          console.log(`⏳ [JoinMeetingDialog] Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          
          // Re-check connection before retry
          if (!this.signalRService.isConnected()) {
            console.log('🔄 [JoinMeetingDialog] Connection lost, restarting...');
            await this.signalRService.startConnection();
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } else {
          console.error('❌ [JoinMeetingDialog] Failed to join meeting after all retries');
          this.toasterService.showError('Failed to join meeting. Please refresh the page.', 'Join Error');
        }
      }
    }
  }
}

